import {
  CutoffSettings,
  ExportImageData,
  SnackAdminOrder,
  SnackCategory,
  SnackCategorySubmitType,
  SnackMenu,
  SnackAvailabilityData,
  SnackAvailabilitySelection,
  SnackMenuItem,
  SnackOrder,
  SnackLifecycleMode,
  SnackOrderFormType,
  SnackOrderStatus,
  SnackOrderEditResult,
  SnackOrderStatusUpdate,
  SnackSupplier,
  SnackTimeframeFilter,
  SnackTimeframeFilterValue,
  SupplierFoodSettings,
} from "@/types";
import { isFromToDateFilter } from "@/lib";
import client, { throwCaughtError } from "../client";
import { useBuildingQuery } from "@/components/RouterSetup";
import type { Query } from "@tanstack/react-query";

const lifecycleRefreshOptions = {
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};
const lifecycleDetailInterval = (query: {
  state: { data?: { lifecycleMode?: SnackLifecycleMode } | null };
}) =>
  query.state.data?.lifecycleMode === "delivery-closure-v1" ? 30_000 : false;

const getSnackSupplier = async (
  supplierCompanyId?: string,
): Promise<SnackSupplier | undefined> => {
  try {
    const response = await client.get(
      `/getSnackSupplier?supplierCompanyId=${supplierCompanyId}`,
    );
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
  return undefined;
};

export const snackSupplierOptions = (supplierCompanyId?: string) => ({
  queryKey: ["supplier", supplierCompanyId],
  queryFn: () => getSnackSupplier(supplierCompanyId),
  enabled: !!supplierCompanyId?.trim(),
});

const EMPTY_ITEM_TEXT = { en: "", no: "" };

const getSnackMenuItems = async (
  supplierCompanyId?: string,
  adminPage?: boolean,
): Promise<SnackMenu | null> => {
  try {
    const response = await client.get(
      `/getSnackMenuItems?supplierCompanyId=${supplierCompanyId}&adminPage=${String(adminPage)}`,
    );
    const menu: SnackMenu | null = response.data;
    // cutoff-v1 leaves out empty item text, but the UI reads it unconditionally
    return (
      menu && {
        ...menu,
        items: menu.items?.map((item) => ({
          ...item,
          name: item.name ?? EMPTY_ITEM_TEXT,
          description: item.description ?? EMPTY_ITEM_TEXT,
        })),
      }
    );
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const snackMenuItemsOptions = (
  supplierCompanyId?: string,
  adminPage?: boolean,
) => ({
  queryKey: ["menu-items", supplierCompanyId, adminPage],
  queryFn: () => getSnackMenuItems(supplierCompanyId, adminPage),
  enabled: !!supplierCompanyId?.trim(),
});

const getSnackMenu = async (
  supplierCompanyId?: string,
): Promise<Omit<SnackMenu, "items"> | null> => {
  try {
    const response = await client.get(
      `/getSnackMenu?supplierCompanyId=${supplierCompanyId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const snackMenuOptions = (supplierCompanyId?: string) => ({
  queryKey: ["menu", supplierCompanyId],
  queryFn: () => getSnackMenu(supplierCompanyId),
  enabled: !!supplierCompanyId?.trim(),
});

const getSnackOrder = async (
  orderId: string,
  companyId: string,
  supplierCompanyId?: string,
): Promise<SnackOrder | null> => {
  try {
    const response = await client.get(
      `/getSnackOrder?orderId=${orderId}&buildingTenantId=${companyId}&supplierCompanyId=${supplierCompanyId}`,
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getSnackOrderOptions = (
  orderId: string,
  companyId: string,
  supplierCompanyId?: string,
  userId?: string,
) => ({
  queryKey: ["order", orderId, companyId, supplierCompanyId, userId],
  queryFn: () => getSnackOrder(orderId, companyId, supplierCompanyId),
  enabled:
    !!orderId?.trim() && !!companyId?.trim() && !!supplierCompanyId?.trim(),
  ...lifecycleRefreshOptions,
  refetchInterval: lifecycleDetailInterval,
});

export const useSnackOrder = (
  orderId: string,
  companyId: string,
  supplierCompanyId?: string,
  userId?: string,
) => {
  return useBuildingQuery(
    getSnackOrderOptions(orderId, companyId, supplierCompanyId, userId),
  );
};

const getUserSnackOrders = async (
  userId: string,
  buildingTenantId: string,
): Promise<SnackOrder[]> => {
  try {
    const response = await client.get(
      `/getUserSnackOrders?userId=${userId}&buildingTenantId=${buildingTenantId}`,
    );
    return response.data as SnackOrder[];
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getUserSnackOrdersOptions = (
  userId: string,
  buildingTenantId: string,
  lifecycleMode?: SnackLifecycleMode,
) => ({
  queryKey: ["user-orders", userId, buildingTenantId],
  queryFn: () => getUserSnackOrders(userId, buildingTenantId),
  enabled: !!(userId && buildingTenantId),
  ...lifecycleRefreshOptions,
  // Supplier mode keeps an empty active list polling too. Existing rows also
  // establish the mode if the supplier read has not completed.
  refetchInterval: (query: Query<SnackOrder[]>) =>
    lifecycleMode === "delivery-closure-v1" ||
    query.state.data?.some(
      (order) => order.lifecycleMode === "delivery-closure-v1",
    )
      ? 30_000
      : false,
});

const getAllSnackOrders = async (
  supplierCompanyId?: string,
  timeframe?: SnackTimeframeFilterValue,
): Promise<SnackOrder[]> => {
  try {
    const timeFilter = isFromToDateFilter(timeframe)
      ? `&from=${timeframe.from}&to=${timeframe.to}`
      : `&timeframe=${timeframe}`;
    const response = await client.get(
      `/getAllSnackOrders?supplierCompanyId=${supplierCompanyId}${timeFilter}`,
    );
    return response.data as SnackOrder[];
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getAllSnackOrdersOptions = (
  supplierCompanyId?: string,
  timeframe: SnackTimeframeFilterValue = SnackTimeframeFilter.NEXT_30_DAYS,
) => ({
  queryKey: ["all-orders", supplierCompanyId, timeframe],
  queryFn: () => getAllSnackOrders(supplierCompanyId, timeframe),
  enabled:
    !!supplierCompanyId &&
    (isFromToDateFilter(timeframe) ||
      (typeof timeframe === "string" && !!timeframe)),
  refetchInterval: 2500,
});

const getSnackOrderDetails = async (
  supplierCompanyId?: string,
  orderId?: string,
): Promise<SnackAdminOrder | null> => {
  try {
    const response = await client.get(
      `/getSnackOrderDetails?supplierCompanyId=${supplierCompanyId}&orderId=${orderId}`,
    );
    return response.data as SnackAdminOrder;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getSnackOrderDetailsOptions = (
  supplierCompanyId?: string,
  orderId?: string,
) => ({
  queryKey: ["snack-order-details", orderId],
  queryFn: () => getSnackOrderDetails(supplierCompanyId, orderId),
  enabled: !!(supplierCompanyId && orderId),
  ...lifecycleRefreshOptions,
  refetchInterval: lifecycleDetailInterval,
});

export const useSnackOrderDetails = (
  supplierCompanyId?: string,
  orderId?: string,
) => {
  return useBuildingQuery(
    getSnackOrderDetailsOptions(supplierCompanyId, orderId),
  );
};

export interface CancelOrderPayload {
  orderId: string;
  userId: string;
  userEmail: string;
}
export type SnackOrderCancellationResponse = {
  code: "ORDER_CANCELLED";
  data: Pick<
    SnackOrderStatusUpdate,
    "id" | "orderNumber" | "status" | "statusUpdatedAt"
  >;
};
export const cancelSnackOrder = async ({
  orderId,
  userId,
  userEmail,
}: CancelOrderPayload) => {
  return await client.put<SnackOrderCancellationResponse>("/cancelSnackOrder", {
    orderId,
    userId,
    userEmail,
  });
};

export const createSnackOrder = async (orderData: SnackOrderFormType) => {
  const response = await client.post(`/createSnackOrder`, orderData);
  return response.data.id;
};

export type SnackAvailabilityResult = {
  data: SnackAvailabilityData;
  roundTripMs: number;
  /** `performance.now()` when the response arrived */
  receivedAt: number;
};

const checkSnackAvailability = async (
  userId: string,
  selection: SnackAvailabilitySelection,
): Promise<SnackAvailabilityResult> => {
  const startedAt = performance.now();
  try {
    const response = await client.post(`/checkSnackAvailability`, {
      userId,
      ...selection,
    });
    const receivedAt = performance.now();
    return {
      data: response.data.data,
      roundTripMs: receivedAt - startedAt,
      receivedAt,
    };
  } catch (error) {
    return throwCaughtError(error);
  }
};

/**
 * Which items can be ordered for a cut-off v1 selection. The key covers the
 * whole selection, so a response for a selection the user has since changed
 * never lands on the current one.
 *
 * @param userId - The user the order is for; the permission guard checks it.
 */
export const snackAvailabilityOptions = (
  supplierCompanyId: string | undefined,
  userId: string,
  selection: SnackAvailabilitySelection | null,
) => ({
  queryKey: [
    "snack-availability",
    supplierCompanyId,
    userId,
    selection?.deliveryTime,
    selection?.deliveryType,
    selection ? [...selection.selectedItemIds].sort().join(",") : null,
  ],
  queryFn: () => checkSnackAvailability(userId, selection!),
  enabled: !!supplierCompanyId?.trim() && !!selection,
});

export const updateSnackOrder = async (
  orderId: string,
  updates: Pick<
    SnackOrderFormType,
    | "items"
    | "deliveryType"
    | "deliveryTime"
    | "invoiceReference"
    | "participantsCount"
  >,
) => {
  return await client.put<SnackOrderEditResult>(`/updateSnackOrder`, {
    orderId,
    ...updates,
  });
};

export const updateSnackOrderStatus = async (
  orderId: string,
  newStatus: SnackOrderStatus,
  statusNotes?: string,
) => {
  return await client.put<SnackOrderStatusUpdate>(`/updateSnackOrderStatus`, {
    orderId,
    newStatus,
    statusNotes,
  });
};

export const updateSnackSupplier = async (
  updateData: Partial<
    Omit<SnackSupplier, "wlcmFoodSettings"> & {
      wlcmFoodSettings: Partial<SupplierFoodSettings>;
    }
  >,
) => {
  return await client.put(`/updateSnackSupplier`, updateData);
};

export const updateSnackMenuItem = async (
  menuItemId: string,
  updateData: Partial<SnackMenuItem>,
) => {
  return await client.put(
    `/updateSnackMenuItem?menuItemId=${menuItemId}`,
    updateData,
  );
};

export const createSnackMenuItem = async (
  menuId: string,
  itemData: Partial<SnackMenuItem>,
) => {
  return await client.post(`/createSnackMenuItem?menuId=${menuId}`, itemData);
};

export const autoUpdateSnackOrderStatuses = async (
  supplierCompanyId?: string,
) => {
  return await client.post(`/autoUpdateOrderStatuses`, {
    supplierCompanyId,
  });
};

export const generateSnackImage = async (
  subject: string,
  description?: string,
): Promise<ExportImageData | null> => {
  try {
    const response = await client.post(`/generateSnackImage`, {
      subject,
      description,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const generateMenuItem = async (prompt: string) => {
  return await client.post("/generateMenuItem", {
    prompt,
  });
};

export const createSnackMenu = async (
  menuData: Omit<SnackMenu, "id" | "categories" | "items"> & {
    categories: Array<SnackCategory | SnackCategorySubmitType>;
  },
) => {
  return await client.post("/createSnackMenu", { ...menuData, items: [] });
};

export const updateSnackMenu = async (
  menuId: string,
  menuData: Omit<SnackMenu, "id" | "categories" | "items"> & {
    categories: Array<SnackCategory | SnackCategorySubmitType>;
  },
) => {
  return await client.put(`/updateSnackMenu?menuId=${menuId}`, menuData);
};

export const updateCategoryVisibility = async (
  menuId: string,
  categoryId: string,
  isHidden: boolean,
) => {
  return await client.put(`/updateCategoryVisibility`, {
    menuId,
    categoryId,
    isHidden,
  });
};

/** `cutoffSettings: null` makes every item follow the restaurant default again */
export const updateMenuItemsCutoff = async (
  menuId: string,
  menuItemIds: string[],
  cutoffSettings: CutoffSettings | null,
) => {
  return await client.put(`/updateMenuItemsCutoff`, {
    menuId,
    menuItemIds,
    cutoffSettings,
  });
};
